import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Environment, AssistantItem, AVAILABLE_ASSISTANTS } from '../../../../models/customer-data.model';
import { CustomerManagerService } from '../../../../services/customer-manager.service';

@Component({
  selector: 'app-environment-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './environment-management.component.html',
  styleUrl: './environment-management.component.scss'
})
export class EnvironmentManagementComponent implements OnInit {

  // 資料與狀態
  environments: Environment[] = [];
  filteredEnvironments: Environment[] = [];
  selectedEnvironment: Environment | null = null;
  isEditing = false;
  isLoading = false;
  showModal = false;

  // AI助手管理對話視窗
  showAssistantModal = false;
  availableAssistants: AssistantItem[] = [];
  companyAssistants: AssistantItem[] = [];
  selectedCompanyId = '';

  // 批次操作相關屬性
  selectedAvailableAssistants: Set<string> = new Set(); // 選中的可用助手
  selectedCompanyAssistants: Set<string> = new Set(); // 選中的已擁有助手
  selectAllAvailable = false; // 全選可用助手
  selectAllCompany = false; // 全選已擁有助手

  // 刪除密碼驗證
  showDeleteModal = false;
  deletePassword = '';
  deletePasswordError = '';
  environmentToDelete: Environment | null = null;

  // 表單
  environmentForm: FormGroup;

  // 搜尋與分頁
  searchTerm = '';
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;

  constructor(
    private fb: FormBuilder,
    private customerManagerService: CustomerManagerService
  ) {
    this.environmentForm = this.createForm();
  }

  ngOnInit(): void {
    this.loadEnvironments();
  }

  /**
   * 建立表單
   */
  createForm(): FormGroup {
    return this.fb.group({
      // 合併客戶ID和公司ID為一個欄位
      companyid: ['', [Validators.required, Validators.minLength(2)]],
      domain: ['', [Validators.required, Validators.minLength(2)]],
      // 設定預設值
      production: [true], // 固定為正式環境
      title: ['', [Validators.required]],
      favicon: ['assets/images/JSAI/JS-logo-circle.svg', [Validators.required]], // 設定預設值
      // 隱藏但設定預設值
      apiUrl: ['https://quallicoapi-jp.azurewebsites.net'], // 預設值，不顯示
      companyname: ['', [Validators.required]],
      copyright: ['', [Validators.required]],
      logoUrl: [''], // 非必填，可為空
      Login_title: ['<h1>公司名稱</h1> <h3>系統副標題</h3>', [Validators.required]], // 使用placeholder作為預設值
      css_file_name: ['Default.css', [Validators.required]], // 有預設值但可編輯
      // 固定預設值
      assistantid: ['asst_Eoq4YShI4IJex9m6Ni8XUkM6'],
      version: ['', [Validators.required]],
      // 安全設定
      mfaEnabled: [false],
      // 登入頁面設定
      login_title_text: ['歡迎來到系統', [Validators.required]], // 使用placeholder作為預設值
      login_id_text: ['帳號', [Validators.required]], // 使用placeholder作為預設值
      login_pw_text: ['密碼', [Validators.required]], // 使用placeholder作為預設值
      login_text: ['點此登入', [Validators.required]], // 使用placeholder作為預設值
      login_forget_text: ['忘記密碼?', [Validators.required]], // 使用placeholder作為預設值
      // Azure Blob 設定 - 固定預設值
      blobUrl: ['https://yhaistorage.blob.core.windows.net'],
      blobsasToken: ['sv=2022-11-02&ss=bfqt&srt=co&sp=rwdlacupiytfx&se=2025-12-10T14:37:33Z&st=2024-12-10T06:37:33Z&spr=https&sig=92bNvRtw3gCp%2BT7Cn2pkMnQMHB3BI2xyCbbxwUYTDFM%3D'],
      privateContainerName: [''],
      publicContainerName: ['yuhsine']
    });
  }

  /**
   * 載入環境資料
   */
  loadEnvironments(): void {
    this.isLoading = true;

    this.customerManagerService.getEnvironments(this.searchTerm, this.currentPage, this.itemsPerPage)
      .subscribe({
        next: (response) => {

          console.log('載入環境資料:', response);

          // 為每個環境添加模組配置資料，從實際的模組管理中獲取助手數
          this.environments = response.data.map(env => ({
            ...env,
            departmentCount: 0,
            moduleCount: 0,
            showNoPermission: false,
            moduleConfig: undefined
          }));

          // 載入每個環境的實際模組配置資料
          this.loadModuleCountsForEnvironments();

          this.filteredEnvironments = this.environments;
          this.totalItems = response.total;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('載入環境資料時發生錯誤:', error);
          this.isLoading = false;
          // 可以在這裡顯示錯誤訊息給使用者
          alert('載入環境資料失敗: ' + error);
        }
      });
  }

  /**
   * 套用搜尋篩選
   */
  applyFilters(): void {
    this.currentPage = 1;
    this.loadEnvironments();
  }

  /**
   * 獲取當前頁面的項目
   */
  getCurrentPageItems(): Environment[] {
    // 由於我們現在使用後端分頁，直接返回已篩選的資料
    return this.filteredEnvironments;
  }

  /**
   * 獲取總頁數
   */
  getTotalPages(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  /**
   * 新增環境
   */
  addEnvironment(): void {
    this.selectedEnvironment = null;
    this.isEditing = false;
    this.environmentForm.reset({
      production: true, // 固定為正式環境
      mfaEnabled: false,
      version: '1.0.0',
      css_file_name: 'Default.css', // 預設值
      favicon: 'assets/images/JSAI/JS-logo-circle.svg', // 預設值
      logoUrl: '', // 可為空
      Login_title: '<h1>公司名稱</h1> <h3>系統副標題</h3>', // 預設值
      login_title_text: '歡迎來到系統', // 預設值
      login_id_text: '帳號', // 預設值
      login_pw_text: '密碼', // 預設值
      login_text: '點此登入', // 預設值
      login_forget_text: '忘記密碼?', // 預設值
      assistantid: 'asst_Eoq4YShI4IJex9m6Ni8XUkM6',
      apiUrl: 'https://quallicoapi-jp.azurewebsites.net',
      blobUrl: 'https://yhaistorage.blob.core.windows.net',
      blobsasToken: 'sv=2022-11-02&ss=bfqt&srt=co&sp=rwdlacupiytfx&se=2025-12-10T14:37:33Z&st=2024-12-10T06:37:33Z&spr=https&sig=92bNvRtw3gCp%2BT7Cn2pkMnQMHB3BI2xyCbbxwUYTDFM%3D',
      privateContainerName: '',
      publicContainerName: 'yuhsine'
    });
    
    // 在新增模式下確保公司ID欄位是可編輯的
    this.environmentForm.get('companyid')?.enable();
    
    this.showModal = true;
  }

  /**
   * 編輯環境
   */
  editEnvironment(environment: Environment): void {
    this.selectedEnvironment = environment;
    this.isEditing = true;
    this.populateForm(environment);
    
    // 在編輯模式下禁用公司ID欄位
    this.environmentForm.get('companyid')?.disable();
    
    this.showModal = true;
  }

  /**
   * 填充表單資料
   */
  populateForm(environment: Environment): void {
    this.environmentForm.patchValue({
      // 使用companyid作為統一ID
      companyid: environment.companyid || environment.clientId,
      domain: environment.domain,
      production: true, // 固定為正式環境
      title: environment.title,
      favicon: environment.favicon || 'assets/images/JSAI/JS-logo-circle.svg', // 如果沒有值則使用預設值
      apiUrl: environment.apiUrl || 'https://quallicoapi-jp.azurewebsites.net',
      companyname: environment.companyname,
      copyright: environment.copyright,
      logoUrl: environment.logoUrl || '', // 可為空
      Login_title: environment.Login_title || '<h1>公司名稱</h1> <h3>系統副標題</h3>', // 如果沒有值則使用預設值
      css_file_name: environment.css_file_name || 'Default.css', // 如果沒有值則使用預設值
      assistantid: 'asst_Eoq4YShI4IJex9m6Ni8XUkM6', // 固定值
      version: environment.version,
      mfaEnabled: environment.security?.MFA || false,
      login_title_text: environment.login_page_setting.title_text || '歡迎來到系統', // 如果沒有值則使用預設值
      login_id_text: environment.login_page_setting.id_text || '帳號', // 如果沒有值則使用預設值
      login_pw_text: environment.login_page_setting.pw_text || '密碼', // 如果沒有值則使用預設值
      login_text: environment.login_page_setting.login_text || '點此登入', // 如果沒有值則使用預設值
      login_forget_text: environment.login_page_setting.forget_text || '忘記密碼?', // 如果沒有值則使用預設值
      // Azure Blob 固定值
      blobUrl: 'https://yhaistorage.blob.core.windows.net',
      blobsasToken: 'sv=2022-11-02&ss=bfqt&srt=co&sp=rwdlacupiytfx&se=2025-12-10T14:37:33Z&st=2024-12-10T06:37:33Z&spr=https&sig=92bNvRtw3gCp%2BT7Cn2pkMnQMHB3BI2xyCbbxwUYTDFM%3D',
      privateContainerName: '',
      publicContainerName: 'yuhsine'
    });
  }

  /**
   * 儲存環境
   */
  saveEnvironment(): void {
    if (this.environmentForm.valid) {
      const formValue = this.environmentForm.value;
      
      // 在編輯模式下，如果公司ID被禁用，需要從getRawValue()取得值
      const companyId = this.isEditing ? 
        this.environmentForm.getRawValue().companyid : 
        formValue.companyid;

      const environmentData: Omit<Environment, 'id' | 'createdAt' | 'updatedAt'> = {
        // 客戶ID和公司ID都要有，且使用同一個值
        clientId: companyId,
        companyid: companyId,
        domain: formValue.domain,
        production: true, // 固定為正式環境
        title: formValue.title,
        favicon: formValue.favicon,
        apiUrl: 'https://quallicoapi-jp.azurewebsites.net', // 使用固定值
        companyname: formValue.companyname,
        copyright: formValue.copyright,
        logoUrl: formValue.logoUrl,
        Login_title: formValue.Login_title,
        security: {
          MFA: formValue.mfaEnabled
        },
        login_page_setting: {
          title_text: formValue.login_title_text,
          id_text: formValue.login_id_text,
          pw_text: formValue.login_pw_text,
          login_text: formValue.login_text,
          forget_text: formValue.login_forget_text
        },
        css_file_name: formValue.css_file_name, // 從表單取得值
        assistantid: 'asst_Eoq4YShI4IJex9m6Ni8XUkM6', // 固定值
        AzureBlob: {
          blobUrl: 'https://yhaistorage.blob.core.windows.net',
          blobsasToken: 'sv=2022-11-02&ss=bfqt&srt=co&sp=rwdlacupiytfx&se=2025-12-10T14:37:33Z&st=2024-12-10T06:37:33Z&spr=https&sig=92bNvRtw3gCp%2BT7Cn2pkMnQMHB3BI2xyCbbxwUYTDFM%3D',
          PrivateContainerName: '',
          PublicContainerName: 'yuhsine'
        },
        version: formValue.version
      };

      if (this.isEditing && this.selectedEnvironment?.id) {
        // 更新現有環境
        this.customerManagerService.updateEnvironment(this.selectedEnvironment.id, environmentData)
          .subscribe({
            next: (updatedEnvironment) => {
              this.loadEnvironments();
              this.closeModal();
              alert('環境更新成功！');
            },
            error: (error) => {
              console.error('更新環境時發生錯誤:', error);
              alert('環境更新失敗: ' + error);
            }
          });
      } else {
        // 新增環境
        this.customerManagerService.createEnvironment(environmentData)
          .subscribe({
            next: (newEnvironment) => {
              this.loadEnvironments();
              this.closeModal();
              alert('環境新增成功！');
            },
            error: (error) => {
              console.error('新增環境時發生錯誤:', error);
              alert('環境新增失敗: ' + error);
            }
          });
      }
    } else {
      alert('請填寫所有必要欄位');
    }
  }

  /**
   * 開始刪除環境流程 - 開啟密碼驗證對話視窗
   */
  deleteEnvironment(environment: Environment): void {
    this.environmentToDelete = environment;
    this.deletePassword = '';
    this.deletePasswordError = '';
    this.showDeleteModal = true;
  }

  /**
   * 確認刪除環境 - 驗證密碼後執行刪除
   */
  confirmDelete(event?: Event): void {
    // 防止事件冒泡和預設行為
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.deletePassword === '5200') {
      if (this.environmentToDelete?.id && this.environmentToDelete?.companyid) {

        console.log('確認刪除環境:', this.environmentToDelete);
        this.customerManagerService.deleteEnvironment(this.environmentToDelete.id,this.environmentToDelete.companyid)
          .subscribe({
            next: () => {
              this.loadEnvironments();
              this.closeDeleteModal();
              alert('環境刪除成功！');
            },
            error: (error) => {
              console.error('刪除環境時發生錯誤:', error);
              alert('環境刪除失敗: ' + error);
            }
          });
      }
    } else {
      this.deletePasswordError = '密碼錯誤，請重新輸入';
    }
  }

  /**
   * 關閉刪除密碼驗證對話視窗
   */
  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.environmentToDelete = null;
    this.deletePassword = '';
    this.deletePasswordError = '';
  }

  /**
   * 開啟AI助手管理對話視窗
   */
  openAssistantModal(environment: Environment): void {
    this.selectedCompanyId = environment.companyid;
    this.loadAssistantData(environment);
    this.showAssistantModal = true;
    // 重置選取狀態
    this.resetSelections();
  }

  /**
   * 重置選取狀態
   */
  resetSelections(): void {
    this.selectedAvailableAssistants.clear();
    this.selectedCompanyAssistants.clear();
    this.selectAllAvailable = false;
    this.selectAllCompany = false;
  }

  /**
   * 切換可用助手的選取狀態
   */
  toggleAvailableAssistant(assistantNo: string): void {
    if (this.selectedAvailableAssistants.has(assistantNo)) {
      this.selectedAvailableAssistants.delete(assistantNo);
    } else {
      this.selectedAvailableAssistants.add(assistantNo);
    }
    this.updateSelectAllAvailable();
  }

  /**
   * 切換已擁有助手的選取狀態
   */
  toggleCompanyAssistant(assistantNo: string): void {
    if (this.selectedCompanyAssistants.has(assistantNo)) {
      this.selectedCompanyAssistants.delete(assistantNo);
    } else {
      this.selectedCompanyAssistants.add(assistantNo);
    }
    this.updateSelectAllCompany();
  }

  /**
   * 全選/取消全選可用助手
   */
  toggleSelectAllAvailable(): void {
    this.selectAllAvailable = !this.selectAllAvailable;
    this.selectedAvailableAssistants.clear();

    if (this.selectAllAvailable) {
      this.availableAssistants.forEach(assistant => {
        this.selectedAvailableAssistants.add(assistant.assistantNo);
      });
    }
  }

  /**
   * 全選/取消全選已擁有助手
   */
  toggleSelectAllCompany(): void {
    this.selectAllCompany = !this.selectAllCompany;
    this.selectedCompanyAssistants.clear();

    if (this.selectAllCompany) {
      this.companyAssistants.forEach(assistant => {
        this.selectedCompanyAssistants.add(assistant.assistantNo);
      });
    }
  }

  /**
   * 更新可用助手的全選狀態
   */
  updateSelectAllAvailable(): void {
    this.selectAllAvailable = this.availableAssistants.length > 0 &&
      this.selectedAvailableAssistants.size === this.availableAssistants.length;
  }

  /**
   * 更新已擁有助手的全選狀態
   */
  updateSelectAllCompany(): void {
    this.selectAllCompany = this.companyAssistants.length > 0 &&
      this.selectedCompanyAssistants.size === this.companyAssistants.length;
  }

  /**
   * 批次加入選中的助手
   */
  batchAddAssistants(): void {
    const assistantsToAdd = this.availableAssistants.filter(assistant =>
      this.selectedAvailableAssistants.has(assistant.assistantNo)
    );

    assistantsToAdd.forEach(assistant => {
      // 從可用列表移除
      this.availableAssistants = this.availableAssistants.filter(a => a.assistantNo !== assistant.assistantNo);
      // 加入到公司列表
      this.companyAssistants.push({ ...assistant, enable: true });
    });

    // 重置選取狀態
    this.resetSelections();
  }

  /**
   * 批次移除選中的助手
   */
  batchRemoveAssistants(): void {
    const assistantsToRemove = this.companyAssistants.filter(assistant =>
      this.selectedCompanyAssistants.has(assistant.assistantNo)
    );

    assistantsToRemove.forEach(assistant => {
      // 從公司列表移除
      this.companyAssistants = this.companyAssistants.filter(a => a.assistantNo !== assistant.assistantNo);
      // 加入到可用列表
      this.availableAssistants.push({ ...assistant, enable: false });
    });

    // 重置選取狀態
    this.resetSelections();
  }

  /**
   * 檢查是否有選中的可用助手
   */
  hasSelectedAvailableAssistants(): boolean {
    return this.selectedAvailableAssistants.size > 0;
  }

  /**
   * 檢查是否有選中的已擁有助手
   */
  hasSelectedCompanyAssistants(): boolean {
    return this.selectedCompanyAssistants.size > 0;
  }

  /**
   * 載入AI助手資料
   */
  loadAssistantData(environment: Environment): void {
    // 取得已擁有的助手
    this.companyAssistants = environment.moduleConfig?.lobby ? [...environment.moduleConfig.lobby] : [];

    // 取得可用但未擁有的助手
    this.availableAssistants = AVAILABLE_ASSISTANTS.filter(assistant =>
      !this.companyAssistants.some(companyAssistant => companyAssistant.assistantNo === assistant.assistantNo)
    ).map(assistant => ({ ...assistant }));
  }

  /**
   * 新增助手到公司
   */
  addAssistantToCompany(assistant: AssistantItem): void {
    // 從可用列表移除
    this.availableAssistants = this.availableAssistants.filter(a => a.assistantNo !== assistant.assistantNo);
    // 加入到公司列表
    this.companyAssistants.push({ ...assistant, enable: true });
  }

  /**
   * 從公司移除助手
   */
  removeAssistantFromCompany(assistant: AssistantItem): void {
    // 從公司列表移除
    this.companyAssistants = this.companyAssistants.filter(a => a.assistantNo !== assistant.assistantNo);
    // 加入到可用列表
    this.availableAssistants.push({ ...assistant, enable: false });
  }

  /**
   * 儲存助手配置
   */
  saveAssistantConfig(): void {
    // 取得所有已擁有助手的assistantNo陣列
    const assistantNumbers = this.companyAssistants.map(assistant => assistant.assistantNo);

    console.log('準備儲存助手配置:', this.selectedCompanyId, assistantNumbers);

    // 呼叫API更新助手配置
    this.customerManagerService.updateAssistants(this.selectedCompanyId, assistantNumbers)
      .subscribe({
        next: (response: any) => {
          console.log('助手配置更新成功:', response);

          // 更新本地資料
          const environment = this.environments.find(env => env.companyid === this.selectedCompanyId);
          if (environment && environment.moduleConfig) {
            environment.moduleConfig.lobby = [...this.companyAssistants];
            environment.moduleCount = this.companyAssistants.length;
          }

          this.closeAssistantModal();

          // 根據新的API回應格式顯示詳細訊息
          const addedCount = response.data?.addedCount || 0;
          const removedCount = response.data?.removedCount || 0;
          const totalCount = response.data?.newTotalCount || this.companyAssistants.length;

          let message = '助手配置已儲存！\n';

          if (addedCount > 0) {
            message += `✓ 新增了 ${addedCount} 個助手\n`;
          }

          if (removedCount > 0) {
            message += `✓ 移除了 ${removedCount} 個助手\n`;
          }

          if (addedCount === 0 && removedCount === 0) {
            message += '✓ 助手配置已是最新狀態，無需變更\n';
          }

          message += `目前總共有 ${totalCount} 個助手。`;

          alert(message);
        },
        error: (error: any) => {
          console.error('儲存助手配置時發生錯誤:', error);
          alert('儲存助手配置失敗: ' + (error.error?.message || error.message || '未知錯誤'));
        }
      });
  }

  /**
   * 關閉AI助手管理對話視窗
   */
  closeAssistantModal(): void {
    this.showAssistantModal = false;
    this.selectedCompanyId = '';
    this.availableAssistants = [];
    this.companyAssistants = [];
    // 重置選取狀態
    this.resetSelections();
  }

  /**
   * 關閉模態框
   */
  closeModal(): void {
    this.showModal = false;
    this.selectedEnvironment = null;
    this.isEditing = false;
    
    // 重置表單並確保所有控制項都被啟用
    this.environmentForm.reset();
    this.environmentForm.get('companyid')?.enable();
  }

  /**
   * 生成ID
   */
  generateId(): string {
    return Date.now().toString();
  }

  /**
   * 搜尋按鈕點擊處理
   */
  onSearchClick(): void {
    this.applyFilters();
  }

  /**
   * 搜尋變更處理（移除自動觸發搜尋）
   */
  onSearchChange(): void {
    // 移除自動搜尋，只在按下按鈕或Enter鍵時才搜尋
  }

  /**
   * 頁面變更處理
   */
  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadEnvironments();
  }

  /**
   * 刷新環境資料（供外部呼叫）
   */
  refreshEnvironments(): void {
    this.loadEnvironments();
  }

  /**
   * 載入各環境的實際模組配置數據
   */
  private loadModuleCountsForEnvironments(): void {
    this.environments.forEach(env => {
      // 根據公司ID獲取該公司的模組配置
      this.customerManagerService.getModuleConfigByCompanyId(env.companyid)
        .subscribe({
          next: (moduleConfigResponse: any) => {
            console.log(`載入公司 ${env.companyid} 的模組配置:`, moduleConfigResponse);

            // 處理回應資料，取得 data 屬性中的實際資料
            let moduleConfig = null;

            if (moduleConfigResponse && moduleConfigResponse.data && Array.isArray(moduleConfigResponse.data)) {
              // 取得 data 陣列的第一個元素
              moduleConfig = moduleConfigResponse.data[0];
            }

            // 更新環境的模組配置資料
            if (moduleConfig) {
              env.moduleConfig = moduleConfig;
              env.moduleCount = moduleConfig.lobby?.length || 0;
              env.departmentCount = moduleConfig.departments?.items?.length || 0;
              env.showNoPermission = moduleConfig.show_nopermission || false;

              console.log(`公司 ${env.companyid} 的助手數量: ${env.moduleCount}`);
            } else {
              console.warn(`公司 ${env.companyid} 的模組配置為空`);
              env.moduleCount = 0;
              env.departmentCount = 0;
              env.showNoPermission = false;
            }
          },
          error: (error) => {
            console.warn(`無法載入公司 ${env.companyid} 的模組配置:`, error);
            // 如果無法載入模組配置，保持預設值
            env.moduleCount = 0;
            env.departmentCount = 0;
            env.showNoPermission = false;
          }
        });
    });
  }
}
